import { useState } from "react";
import { PageHero } from "@/pages/About";
import { Field, TextInput, TextArea, SelectField, ConsentCheckbox, Honeypot } from "@/components/FormFields";
import { useFormSubmit } from "@/lib/useFormSubmit";
import { SuccessCard } from "@/pages/WarrenParkConnect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const VOLUNTEER_PROGRAMMES = [
    "Community Engagement",
    "Youth Empowerment",
    "Women Empowerment",
    "Persons with Disabilities Support",
    "Community Enterprise Development",
    "Education & Skills Development",
    "Environmental & Community Clean-up",
    "Food & Livelihood Support",
    "Events & Outreach",
    "Administration & Office Support",
    "ICT & Digital Support",
    "Communications, Photography & Media",
    "Fundraising & Resource Mobilisation",
    "Monitoring & Evaluation",
    "Any Programme (I'm happy to help where needed)",
];
const PARTNERSHIP_TYPES = [
    "Financial support", "In-kind support", "Programme collaboration",
    "Technical expertise", "Volunteer teams", "Multi-year strategic partnership",
];
const SPONSOR_PROJECTS = [
    "Community livelihoods", "Youth empowerment", "Women's empowerment", "Disability inclusion",
    "Golden Years Initiative", "Education", "Health and wellbeing", "Community infrastructure",
    "General support",
];

export default function GetInvolved() {
    return (
        <div>
            <PageHero title="Get Involved" subtitle="Volunteer · Partner · Sponsor" />
            <section className="gsf-section" data-testid="get-involved">
                <div className="gsf-container">
                    <Tabs defaultValue="volunteer" className="w-full">
                        <TabsList className="grid grid-cols-3 max-w-xl bg-[color:var(--gsf-soft-green)] rounded-full p-1" data-testid="get-involved-tabs">
                            <TabsTrigger value="volunteer" className="rounded-full data-[state=active]:bg-[color:var(--gsf-green-700)] data-[state=active]:text-white" data-testid="tab-volunteer">Volunteer</TabsTrigger>
                            <TabsTrigger value="partner" className="rounded-full data-[state=active]:bg-[color:var(--gsf-green-700)] data-[state=active]:text-white" data-testid="tab-partner">Partner</TabsTrigger>
                            <TabsTrigger value="sponsor" className="rounded-full data-[state=active]:bg-[color:var(--gsf-green-700)] data-[state=active]:text-white" data-testid="tab-sponsor">Sponsor</TabsTrigger>
                        </TabsList>
                        <TabsContent value="volunteer" className="mt-10" id="volunteer"><VolunteerForm /></TabsContent>
                        <TabsContent value="partner" className="mt-10" id="partner"><PartnerForm /></TabsContent>
                        <TabsContent value="sponsor" className="mt-10" id="sponsor"><SponsorForm /></TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
}

function VolunteerForm() {
    const [s, setS] = useState({ full_name: "", email: "", phone: "", area: "", skills: "", availability: "", preferred_programme: "", motivation: "", other_interests: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("volunteer");
    if (done) return <SuccessCard testid="volunteer-success">Thank you — a member of our team will be in touch about upcoming volunteer opportunities.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-8 space-y-4 max-w-3xl" data-testid="volunteer-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="v-name" label="Full name" required><TextInput id="v-name" testid="v-name" required value={s.full_name} onChange={(v) => setS({ ...s, full_name: v })} /></Field>
                <Field id="v-email" label="Email" required><TextInput id="v-email" testid="v-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="v-phone" label="Phone / WhatsApp"><TextInput id="v-phone" testid="v-phone" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
                <Field id="v-area" label="Area of residence"><TextInput id="v-area" testid="v-area" value={s.area} onChange={(v) => setS({ ...s, area: v })} /></Field>
            </div>
            <Field id="v-skills" label="Skills"><TextInput id="v-skills" testid="v-skills" value={s.skills} onChange={(v) => setS({ ...s, skills: v })} /></Field>
            <Field id="v-avail" label="Availability"><TextInput id="v-avail" testid="v-avail" value={s.availability} onChange={(v) => setS({ ...s, availability: v })} /></Field>
            <Field id="v-prog" label="Preferred programme">
                <SelectField id="v-prog" testid="v-programme" value={s.preferred_programme} onChange={(v) => setS({ ...s, preferred_programme: v })} placeholder="Select a programme"
                    options={VOLUNTEER_PROGRAMMES} />
            </Field>
            <Field id="v-other" label="Other interests, skills or experience">
                <TextArea id="v-other" testid="v-other-interests" rows={3} value={s.other_interests} onChange={(v) => setS({ ...s, other_interests: v })} />
            </Field>
            <Field id="v-mot" label="Short motivation" required><TextArea id="v-mot" testid="v-motivation" required rows={4} value={s.motivation} onChange={(v) => setS({ ...s, motivation: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="v-consent" />
            {error && <p className="text-sm text-red-600" data-testid="v-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="v-submit">{submitting ? "Submitting…" : "Submit Volunteer Application"}</button>
        </form>
    );
}

function PartnerForm() {
    const [s, setS] = useState({ org: "", contact_person: "", email: "", phone: "", partnership_type: "", resources: "", programme: "", engagement: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("partnership");
    if (done) return <SuccessCard testid="partner-success">Thank you — our partnerships team will follow up.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-8 space-y-4 max-w-3xl" data-testid="partner-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="p-org" label="Organisation name" required><TextInput id="p-org" testid="p-org" required value={s.org} onChange={(v) => setS({ ...s, org: v })} /></Field>
                <Field id="p-cp" label="Contact person" required><TextInput id="p-cp" testid="p-contact" required value={s.contact_person} onChange={(v) => setS({ ...s, contact_person: v })} /></Field>
                <Field id="p-email" label="Email" required><TextInput id="p-email" testid="p-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="p-phone" label="Phone"><TextInput id="p-phone" testid="p-phone" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
            </div>
            <Field id="p-type" label="Type of partnership">
                <SelectField id="p-type" testid="p-type" value={s.partnership_type} onChange={(v) => setS({ ...s, partnership_type: v })} placeholder="Select"
                    options={PARTNERSHIP_TYPES} />
            </Field>
            <Field id="p-res" label="Resources or expertise available"><TextArea id="p-res" testid="p-resources" rows={3} value={s.resources} onChange={(v) => setS({ ...s, resources: v })} /></Field>
            <Field id="p-prog" label="Programme of interest"><TextInput id="p-prog" testid="p-programme" value={s.programme} onChange={(v) => setS({ ...s, programme: v })} /></Field>
            <Field id="p-eng" label="Proposed engagement"><TextArea id="p-eng" testid="p-engagement" rows={3} value={s.engagement} onChange={(v) => setS({ ...s, engagement: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="p-consent" />
            {error && <p className="text-sm text-red-600" data-testid="p-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="p-submit">{submitting ? "Submitting…" : "Submit Partnership Enquiry"}</button>
        </form>
    );
}

function SponsorForm() {
    const [s, setS] = useState({ name: "", email: "", phone: "", org: "", project: "", amount: "", message: "", website: "" });
    const [consent, setConsent] = useState(false);
    const { submit, submitting, done, error } = useFormSubmit("sponsor");
    if (done) return <SuccessCard testid="sponsor-success">Thank you — our team will contact you to discuss sponsorship.</SuccessCard>;
    return (
        <form onSubmit={async (e) => { e.preventDefault(); await submit(s, consent); }} className="gsf-card p-8 space-y-4 max-w-3xl" data-testid="sponsor-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="sp-name" label="Your name" required><TextInput id="sp-name" testid="sp-name" required value={s.name} onChange={(v) => setS({ ...s, name: v })} /></Field>
                <Field id="sp-email" label="Email" required><TextInput id="sp-email" testid="sp-email" type="email" required value={s.email} onChange={(v) => setS({ ...s, email: v })} /></Field>
                <Field id="sp-phone" label="Phone"><TextInput id="sp-phone" testid="sp-phone" value={s.phone} onChange={(v) => setS({ ...s, phone: v })} /></Field>
                <Field id="sp-org" label="Organisation (if applicable)"><TextInput id="sp-org" testid="sp-org" value={s.org} onChange={(v) => setS({ ...s, org: v })} /></Field>
            </div>
            <Field id="sp-project" label="Project to sponsor" required>
                <SelectField id="sp-project" testid="sp-project" value={s.project} onChange={(v) => setS({ ...s, project: v })} placeholder="Select a project"
                    options={SPONSOR_PROJECTS} />
            </Field>
            <Field id="sp-amount" label="Indicative amount (optional)"><TextInput id="sp-amount" testid="sp-amount" value={s.amount} onChange={(v) => setS({ ...s, amount: v })} /></Field>
            <Field id="sp-msg" label="Message"><TextArea id="sp-msg" testid="sp-message" rows={4} value={s.message} onChange={(v) => setS({ ...s, message: v })} /></Field>
            <Honeypot value={s.website} onChange={(v) => setS({ ...s, website: v })} />
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="sp-consent" />
            {error && <p className="text-sm text-red-600" data-testid="sp-error">{error}</p>}
            <button className="gsf-btn-primary disabled:opacity-60" disabled={submitting} data-testid="sp-submit">{submitting ? "Submitting…" : "Submit Sponsorship Enquiry"}</button>
        </form>
    );
}
